import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import Dropdown from 'react-bootstrap/Dropdown';
import './Header.css';
import { getProfilePhotoUrl } from '../utils/imageUtils.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const Header = ({ onToggleSidebar }) => {
  const { user, logout, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [imageKey, setImageKey] = useState(Date.now());

  // Refresh profile data when component mounts to ensure latest data
  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = () => {
      setImageKey(Date.now());
    };
    
    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Function to get profile image with fallback (load from backend)
  const getProfileImage = () => {
    if (user?.profile_photo) {
      const base = getProfilePhotoUrl(user.profile_photo);
      const version = localStorage.getItem('profileImageVersion') || imageKey;
      return `${base}${base.includes('?') ? '&' : '?'}v=${version}`;
    }
    return `${API_BASE_URL}/uploads/user.png`;
  };

  // Show loading state while user data is being fetched
  if (loading) {
    return (
      <header className="header fixed-top d-flex align-items-center">
        <div className="d-flex align-items-center">
          <button 
            className="toggle-sidebar-btn btn btn-link"
            onClick={onToggleSidebar}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 15px' }}
          >
            <i className="bi bi-list" style={{ fontSize: '24px', color: '#000' }}></i>
          </button>
          <Link to="/" className="logo d-flex align-items-center">
            <img src="/assets/img/logo1.png" alt="BuildINT" className="logo-image" />
          </Link>
        </div>
        <nav className="header-nav ms-auto">
          <ul className="d-flex align-items-center" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            <li className="nav-item pe-3" style={{ listStyle: 'none' }}>
              <div className="nav-link nav-profile d-flex align-items-center pe-0">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </li>
          </ul>
        </nav>
      </header>
    );
  }

  return (
    <header className="header fixed-top d-flex align-items-center">
      <div className="d-flex align-items-center">
        <button
          className="toggle-sidebar-btn"
          onClick={onToggleSidebar}
          aria-label="Toggle navigation"
        >
          <i className="bi bi-list"></i>
        </button>
        <Link to="/" className="logo d-flex align-items-center">
          <img src="/assets/img/logo1.png" alt="BuildINT" className="logo-image" />
        </Link>
      </div>

      <nav className="header-nav ms-auto">
        <ul className="d-flex align-items-center" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          <li className="nav-item d-block d-lg-none" style={{ listStyle: 'none' }}>
            <a className="nav-link nav-icon search-bar-toggle" href="#/">
              <i className="bi bi-search"></i>
            </a>
          </li>

          <li className="nav-item pe-3" style={{ listStyle: 'none' }}>
            <Dropdown align="end">
              <Dropdown.Toggle
                as="a"
                className="nav-link nav-profile d-flex align-items-center pe-0"
                style={{ cursor: 'pointer', background: 'none', border: 'none', boxShadow: 'none', listStyle: 'none', textDecoration: 'none' }}
                id="user-profile-dropdown"
              >
                <img 
                  src={getProfileImage()} 
                  alt="Profile" 
                  className="rounded-circle"
                  style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.src = `${API_BASE_URL}/uploads/user.png`;
                  }}
                />
                <span className="d-none d-md-block ps-2">{user?.first_name}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-arrow profile">
                <div className="dropdown-header text-center">
                  <img 
                    src={getProfileImage()} 
                    alt="Profile" 
                    className="rounded-circle mb-2" 
                    style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = `${API_BASE_URL}/uploads/user.png`;
                    }}
                  />
                  <h6 className="mt-2 mb-0">{user?.first_name} {user?.last_name}</h6>
                  <span className="text-muted">{user?.role}</span>
                </div>
                <Dropdown.Divider />
                <Dropdown.Item as={Link} to="/profile" className="d-flex align-items-center">
                  <i className="bi bi-person me-2"></i>
                  <span>My Profile</span>
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item as="button" className="d-flex align-items-center" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right me-2"></i>
                  <span>Log Out</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;